/**
 * `useViewerSlice` — SSR-safe selector hook for `useViewerStore`.
 *
 * Why this wrapper exists:
 *
 * Zustand v5's bound hook (`useViewerStore((s) => ...)`) is implemented
 * as `useSyncExternalStore(subscribe, getState, getInitialState)`. The
 * third argument is `getServerSnapshot` — Zustand wires it to the
 * **initial** store state to keep React hydration consistent across
 * server / client. As a side-effect, `renderToStaticMarkup` always sees
 * the initial state regardless of any `setState` mutations made before
 * the render call. That makes WP-3 component tests that do
 * `useViewerStore.setState({ … }) → renderToStaticMarkup(<Card />)`
 * impossible to drive through the bound hook.
 *
 * The wrapper below uses `getState` for **both** client and server
 * snapshots, so a render performed via `renderToStaticMarkup` after a
 * `setState` call observes the current store state. The viewer never
 * hydrates a `renderToStaticMarkup` output (there is no SSR pipeline in
 * production), so the divergent server snapshot is moot — but if
 * `renderToString` is ever wired into a real SSR pipeline, the same
 * helper still hydrates correctly because client + server snapshots
 * always agree by definition.
 *
 * Citation:
 *   - `node_modules/zustand/react.js` (`useStore`)
 *   - React docs §useSyncExternalStore — getServerSnapshot contract.
 */

import { useSyncExternalStore } from 'react';
import { useViewerStore } from './useViewerStore';

type ViewerState = ReturnType<typeof useViewerStore.getState>;

export function useViewerSlice<T>(selector: (state: ViewerState) => T): T {
  return useSyncExternalStore(
    useViewerStore.subscribe,
    () => selector(useViewerStore.getState()),
    () => selector(useViewerStore.getState()),
  );
}
